import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { Alert, TableSortLabel, Typography, AlertColor } from "@mui/material";
import {
  typography,
  spacing,
  colors,
} from "../../../../../../../../../assets/styles/tokens";
import { useSelector } from "react-redux";

interface Props {
  monthTableHeadings: { [key: string]: string };
}

const RecentMonthTable = ({ monthTableHeadings }: Props) => {
  const headCells = Object.entries(monthTableHeadings);
  const { previousMonthQueryData } = useSelector(
    (state: any) => state.businessLeaderDashBoard,
  );
  const [order, setOrder] = React.useState<"asc" | "desc">("asc");
  const [orderBy, setOrderBy] = React.useState<string>("metric");
  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };
  const formatAttainment = (rawValue: number) =>
    Number(rawValue * 100).toFixed(1);

  /**
   * Formats Previous Month metric values for UI display.
   * Business Rules:
   * - "CVR" is treated as a ratio (e.g., 0.15) and converted to a percentage (15%).
   * - All metrics except "FTD" are rounded to the nearest integer and suffixed with "$".
   * - "FTD" is rounded to the nearest integer with no currency symbol.
   * - null or undefined values return "N/A".
   * @param metricName - The metric identifier (e.g., "CVR", "FTD", "SPEND").
   * @param rawValue - The numeric value returned from the API.
   * @returns A formatted string ready for UI rendering.
   */
  const formatPreviousMonthValue = (metricName: string, rawValue: any) => {
    if (rawValue === null || rawValue === undefined) {
      return "N/A";
    }
    if (metricName.toUpperCase() === "CVR") {
      const value = rawValue * 100;
      return Math.round(Number(value.toFixed(1))) + "%";
    }
    if (metricName.toUpperCase() !== "FTD") {
      return rawValue < 0
        ? `-$${Math.round(Math.abs(rawValue)).toLocaleString()}`
        : "$" + Math.round(rawValue).toLocaleString();
    }
    return Math.round(rawValue).toLocaleString();
  };

  const monthTableRows = React.useMemo(() => {
    return (
      previousMonthQueryData?.query_output?.map((item: any) => ({
        metric: item.Metrics.toUpperCase(),
        rawActual: item.Actual,
        rawTarget: item.Target,
        rawGap: item.Gap,
        rawAttainment: item.Attainment * 100,
        actual_target: `${formatPreviousMonthValue(item.Metrics, item.Actual)} / ${formatPreviousMonthValue(item.Metrics, item.Target)}`,
        gap: formatPreviousMonthValue(item.Metrics, item.Gap),
        attainment: `${formatAttainment(item.Attainment)}%`,
      })) || []
    );
  }, [previousMonthQueryData]);

  const sortedRows = React.useMemo(() => {
    const rows = [...monthTableRows];

    rows.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (orderBy) {
        case "metric":
          aValue = a.metric;
          bValue = b.metric;
          break;
        case "gap":
          aValue = a.rawGap;
          bValue = b.rawGap;
          break;
        case "attainment":
          aValue = a.rawAttainment;
          bValue = b.rawAttainment;
          break;
        case "actual_target":
          aValue = a.rawActual;
          bValue = b.rawActual;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
      return 0;
    });

    return rows;
  }, [monthTableRows, order, orderBy]);
  /**
   * Normalizes a percentage value into a numeric form.
   * Accepts:
   * - number (e.g., 95)
   * - string with percentage symbol (e.g., "95%")
   * Returns:
   * - A numeric percentage value (e.g., 95)
   * - null if parsing fails
   * This ensures consistent processing before applying business
   * threshold logic for alert coloring.
   * @param value - Percentage value as number or string.
   * @returns Parsed numeric percentage or null if invalid.
   */
  const parsePercentage = (value: unknown): number | null => {
    if (typeof value === "number") return value;
    if (typeof value === "string") {
      const parsed = parseFloat(value.replace("%", ""));
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  };

  /**
   * Determines the alert severity and color for the "Attainment" column
   * based on business-specific performance rules.
   * Metric Categories:
   * Negative metrics (lower is better):
   * - spend
   * - cac
   * Rules:
   *   > 100%  → error
   *   90–100% → warning
   *   < 90%   → success
   * Positive metrics (higher is better):
   * - pv
   * - profit
   * - nrpu
   * - ftd
   * - cvr
   * Rules:
   *   < 85%   → error
   *   85–99%  → warning
   *   > 99%   → success
   * @param value - Attainment percentage (already converted to 0–100 scale).
   * @param metric - Metric name used to determine rule set.
   * @returns An object containing:
   *   - severity: MUI AlertColor ("error" | "warning" | "success")
   *   - color: Corresponding design token color value.
   */
  const getTargetAlertConfig = (
    value: number,
    metric: string,
  ): { severity: AlertColor; color: string } => {
    const lowerMetric = metric.toLowerCase();
    const negativeMetrics = ["spend", "cac"];
    const positiveMetrics = ["pv", "profit", "nrpu", "ftd", "cvr"];
    if (negativeMetrics.includes(lowerMetric)) {
      if (value > 100) {
        return { severity: "error", color: colors.Error[600].$value };
      }
      if (value >= 90) {
        return { severity: "warning", color: colors.Warning[600].$value };
      }
      return { severity: "success", color: colors.Success[600].$value };
    }
    if (positiveMetrics.includes(lowerMetric)) {
      if (value < 85) {
        return { severity: "error", color: colors.Error[600].$value };
      }
      if (value < 99) {
        return { severity: "warning", color: colors.Warning[600].$value };
      }
      return { severity: "success", color: colors.Success[600].$value };
    }
    return { severity: "success", color: colors.Success[600].$value };
  };

  return (
    <>
      <Table size="small">
        <TableHead sx={{ background: colors.GrayLightMode[100].$value }}>
          <TableRow>
            {headCells.map(([key, value], index) => (
              <TableCell
                key={index}
                align="left"
                sx={{
                  // whiteSpace: "nowrap",
                  fontSize: typography.Size.Text.xs.$value,
                  fontWeight: typography.Font_weight.Regular.$value,
                  paddingX: spacing.Spacing.xs.$value,
                  paddingY: spacing.Spacing.sm.$value,
                  lineHeight: typography.Line_height.text.xs.$value,
                }}
              >
                {key === "metric" ? (
                  value
                ) : (
                  <TableSortLabel
                    active={orderBy === key}
                    direction={orderBy === key ? order : "asc"}
                    onClick={() => handleRequestSort(key)}
                    sx={{
                      "& .MuiTableSortLabel-icon": {
                        display: "none",
                      },
                    }}
                  >
                    {value}
                  </TableSortLabel>
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedRows.map((row: any) => (
            <React.Fragment key={row.metric}>
              <TableRow
                sx={{
                  "& > *": {
                    borderBottom:
                      "1px solid " +
                      colors.GrayLightMode[200].$value +
                      "!important",
                  },
                }}
              >
                {headCells.map(([key], cellIndex) => (
                  <TableCell
                    key={cellIndex}
                    align="left"
                    sx={{
                      whiteSpace: "nowrap",
                      fontSize: typography.Size.Text.xs.$value,
                      fontWeight: typography.Font_weight.Regular.$value,
                      paddingX: spacing.Spacing.xs.$value,
                      paddingY: spacing.Spacing.xs.$value,
                      lineHeight: typography.Line_height.text.xs.$value,
                    }}
                  >
                    {key === "metric" ? (
                      <>
                        <Typography
                          sx={{
                            fontSize: typography.Size.Text.xs.$value,
                          }}
                        >
                          {row[key] ? row[key] : "N/A"}
                        </Typography>
                      </>
                    ) : key === "actual_target" ? (
                      <Typography
                        sx={{
                          fontWeight: typography.Font_weight.Semibold.$value,
                          fontSize: typography.Size.Text.xs.$value,
                        }}
                      >
                        {" "}
                        {row[key] ? row[key] : "N/A"}
                      </Typography>
                    ) : key === "gap" ? (
                      <Typography
                        sx={{
                          fontWeight: typography.Font_weight.Semibold.$value,
                          fontSize: typography.Size.Text.xs.$value,
                        }}
                      >
                        {row[key] ? row[key] : "N/A"}
                      </Typography>
                    ) : key === "attainment" ? (
                      <div className="AlertItems">
                        {(() => {
                          const value = parsePercentage(row[key]);
                          if (value === null) return <>N/A</>;
                          const { severity, color } = getTargetAlertConfig(
                            value,
                            row.metric,
                          );

                          return (
                            <Alert
                              severity={severity}
                              icon={false}
                              sx={{
                                "& .MuiAlert-message": {
                                  paddingY:
                                    spacing.Spacing.xxs.$value + " !important",
                                  paddingX:
                                    spacing.Spacing.sm.$value + " !important",
                                },
                                color,
                                fontWeight:
                                  typography.Font_weight.Semibold.$value,
                                paddingX: spacing.Spacing.none.$value,
                                paddingY: spacing.Spacing.none.$value,
                                fontSize: typography.Size.Text.xs.$value,
                                lineHeight:
                                  typography.Line_height.text.xs.$value,
                                display: "inline-flex",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {value}%
                            </Alert>
                          );
                        })()}
                      </div>
                    ) : (
                      "N/A"
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </>
  );
};

export default RecentMonthTable;
